import { fetchAuthSession } from 'aws-amplify/auth';
import { API_URL } from '../aws-config';

async function authHeaders(): Promise<Record<string, string>> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString() ?? '';
  return {
    'Content-Type': 'application/json',
    Authorization: token,
  };
}

export async function listSubmissions(status = 'PENDING_REVIEW', limit = 50, fromDate = '', toDate = '') {
  const headers = await authHeaders();
  const params = new URLSearchParams({ status, limit: String(limit) });
  if (fromDate) params.set('from_date', fromDate);
  if (toDate) params.set('to_date', toDate);
  const resp = await fetch(`${API_URL}/submissions?${params}`, { headers });
  if (!resp.ok) throw new Error(`Failed to list submissions: ${resp.status}`);
  return resp.json();
}

export async function getSubmission(id: string) {
  const headers = await authHeaders();
  const resp = await fetch(`${API_URL}/submissions/${id}`, { headers });
  if (!resp.ok) throw new Error(`Failed to get submission ${id}: ${resp.status}`);
  return resp.json();
}

export async function saveAnnotation(submissionId: string, annotation: {
  expert_grade: number | 'ng';
  expert_label?: string;
  defects?: any[];
  notes?: string;
  confidence?: string;
  clean_spine?: boolean;
  clean_corners?: boolean;
}) {
  const headers = await authHeaders();
  const resp = await fetch(`${API_URL}/annotations/${submissionId}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(annotation),
  });
  if (!resp.ok) throw new Error(`Failed to save annotation: ${resp.status}`);
  return resp.json();
}

export async function getStats() {
  const headers = await authHeaders();
  const resp = await fetch(`${API_URL}/stats`, { headers });
  if (!resp.ok) throw new Error(`Failed to get stats: ${resp.status}`);
  return resp.json();
}

export type QueueCategory = 'all' | 'exact' | 'close' | 'disputed' | 'big_dispute';

export async function getMyQueue(category: QueueCategory = 'all', fromDate = '', toDate = '') {
  const headers = await authHeaders();
  const params = new URLSearchParams({ category });
  if (fromDate) params.set('from_date', fromDate);
  if (toDate) params.set('to_date', toDate);
  const resp = await fetch(`${API_URL}/my-queue?${params}`, { headers });
  if (!resp.ok) throw new Error(`Failed to get queue: ${resp.status}`);
  return resp.json();
}

export async function getApprovedEmails() {
  const headers = await authHeaders();
  const resp = await fetch(`${API_URL}/admin/approved-emails`, { headers });
  if (!resp.ok) throw new Error(`Failed to get approved emails: ${resp.status}`);
  return resp.json();
}

export async function manageApprovedEmail(action: 'add' | 'remove', email: string, note?: string) {
  const headers = await authHeaders();
  const resp = await fetch(`${API_URL}/admin/approved-emails`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ action, email, note }),
  });
  if (!resp.ok) throw new Error(`Failed to ${action} email: ${resp.status}`);
  return resp.json();
}

export async function getAdminAnnotations() {
  const headers = await authHeaders();
  const resp = await fetch(`${API_URL}/admin/annotations`, { headers });
  if (!resp.ok) throw new Error(`Failed to get audit log: ${resp.status}`);
  return resp.json();
}

export async function getAnnotatorStats() {
  const headers = await authHeaders();
  const resp = await fetch(`${API_URL}/stats/annotators`, { headers });
  if (!resp.ok) throw new Error(`Failed to get annotator stats: ${resp.status}`);
  return resp.json();
}

export async function bulkDismiss(userGradeGuess: number, minDivergence: number, dryRun: boolean) {
  const headers = await authHeaders();
  const resp = await fetch(`${API_URL}/admin/bulk-dismiss`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ user_grade_guess: userGradeGuess, min_divergence: minDivergence, dry_run: dryRun }),
  });
  if (!resp.ok) throw new Error(`Failed to bulk dismiss: ${resp.status}`);
  return resp.json();
}

export async function markMissed(beforeDate: string) {
  const headers = await authHeaders();
  const resp = await fetch(`${API_URL}/admin/mark-missed`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ before_date: beforeDate }),
  });
  if (!resp.ok) throw new Error(`Failed to mark missed: ${resp.status}`);
  return resp.json();
}

export async function invalidateSubmission(submissionId: string) {
  const headers = await authHeaders();
  const resp = await fetch(`${API_URL}/submissions/${submissionId}/invalidate`, {
    method: 'POST',
    headers,
  });
  if (!resp.ok) throw new Error(`Failed to invalidate submission: ${resp.status}`);
  return resp.json();
}

export async function getProfile() {
  const headers = await authHeaders();
  const resp = await fetch(`${API_URL}/profile`, { headers });
  if (!resp.ok) throw new Error(`Failed to get profile: ${resp.status}`);
  return resp.json();
}

export async function getDefectQueue() {
  const headers = await authHeaders();
  const resp = await fetch(`${API_URL}/admin/defect-queue`, { headers });
  if (!resp.ok) throw new Error(`Failed to get defect queue: ${resp.status}`);
  return resp.json();
}

export async function sendToDefectQueue(submissionId: string, photoIndex: number) {
  const headers = await authHeaders();
  const resp = await fetch(`${API_URL}/admin/defect-labels/${submissionId}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ photo_labels: { [photoIndex]: [] }, inline: true }),
  });
  if (!resp.ok) throw new Error(`Failed to send to defect queue: ${resp.status}`);
  return resp.json();
}

export async function saveDefectLabels(submissionId: string, photoLabels: Record<string, any[]>, inline = false) {
  const headers = await authHeaders();
  const resp = await fetch(`${API_URL}/admin/defect-labels/${submissionId}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ photo_labels: photoLabels, inline }),
  });
  if (!resp.ok) throw new Error(`Failed to save defect labels: ${resp.status}`);
  return resp.json();
}

export async function dismissDefectSubmission(submissionId: string) {
  const headers = await authHeaders();
  const resp = await fetch(`${API_URL}/admin/defect-dismiss/${submissionId}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({}),
  });
  if (!resp.ok) throw new Error(`Failed to dismiss submission: ${resp.status}`);
  return resp.json();
}

export async function lookupSubmission(q: string): Promise<{ submission_id: string }> {
  const headers = await authHeaders();
  const params = new URLSearchParams({ q });
  const resp = await fetch(`${API_URL}/admin/submissions/search?${params}`, { headers });
  if (resp.status === 404) throw new Error('Not found');
  if (!resp.ok) throw new Error(`Search failed: ${resp.status}`);
  return resp.json();
}

export async function saveProfile(nickname: string) {
  const headers = await authHeaders();
  const resp = await fetch(`${API_URL}/profile`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ nickname }),
  });
  if (!resp.ok) throw new Error(`Failed to save profile: ${resp.status}`);
  return resp.json();
}
